import { X } from 'lucide-react'
import { DynamicIcon } from '@/lib/utils/lucide-icon'
import { cn } from '@/lib/utils/cn'
import {
  configFor,
  notificationMessage,
  notificationSnippet,
} from '../lib/notification-catalog'
import type { NotificationRow } from '../api/notifications-api'

const rtf = new Intl.RelativeTimeFormat('fa', { numeric: 'auto' })

export function faTimeAgo(iso: string, now: Date = new Date()): string {
  const seconds = Math.round((new Date(iso).getTime() - now.getTime()) / 1000)
  const abs = Math.abs(seconds)
  if (abs < 60) return 'همین حالا'
  if (abs < 3600) return rtf.format(Math.trunc(seconds / 60), 'minute')
  if (abs < 86400) return rtf.format(Math.trunc(seconds / 3600), 'hour')
  if (abs < 30 * 86400) return rtf.format(Math.trunc(seconds / 86400), 'day')
  return rtf.format(Math.trunc(seconds / (30 * 86400)), 'month')
}

interface NotificationItemProps {
  notification: NotificationRow
  /** DB-catalog label, used when the client build doesn't know the event type */
  catalogLabelFa?: string
  onOpen: (n: NotificationRow) => void
  onDismiss?: (n: NotificationRow) => void
  compact?: boolean
}

export function NotificationItem({
  notification: n,
  catalogLabelFa,
  onOpen,
  onDismiss,
  compact = false,
}: NotificationItemProps) {
  const config = configFor(n.event_type)
  const snippet = notificationSnippet(n)

  return (
    <div
      role="button"
      tabIndex={0}
      dir="rtl"
      onClick={() => onOpen(n)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onOpen(n)
      }}
      className={cn(
        'group relative flex items-start gap-3 w-full text-start cursor-pointer',
        compact ? 'px-3 py-2.5' : 'px-4 py-3',
        'border-b border-border last:border-b-0 transition-colors hover:bg-muted/60',
        !n.is_read && 'bg-primary/5',
        n.is_dismissed && 'opacity-50',
      )}
    >
      <div
        className={cn(
          'mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
          n.priority === 'high'
            ? 'bg-destructive/10 text-destructive'
            : 'bg-muted text-muted-foreground',
          !n.is_read && n.priority !== 'high' && 'bg-primary/10 text-primary',
        )}
      >
        <DynamicIcon name={config.icon} className="h-4 w-4" />
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span
            className={cn(
              'text-xs',
              n.is_read ? 'font-normal text-muted-foreground' : 'font-semibold text-foreground',
            )}
          >
            {catalogLabelFa ?? config.labelFa}
          </span>
          {!n.is_read && <span className="h-1.5 w-1.5 rounded-full bg-primary shrink-0" />}
        </div>
        <p className={cn('mt-0.5 text-sm leading-5 break-words', n.is_read ? 'text-muted-foreground' : 'text-foreground')}>
          {notificationMessage(n, catalogLabelFa)}
        </p>
        {snippet && (
          <p className="mt-1 truncate rounded bg-muted/70 px-2 py-1 text-xs text-muted-foreground">
            «{snippet}»
          </p>
        )}
        <div className="mt-1 flex items-center gap-2 text-[11px] text-muted-foreground">
          {n.actor?.full_name && <span>{n.actor.full_name}</span>}
          {n.actor?.full_name && <span>·</span>}
          <span>{faTimeAgo(n.created_at)}</span>
        </div>
      </div>

      {onDismiss && !n.is_dismissed && (
        <button
          type="button"
          aria-label="حذف اعلان"
          onClick={(e) => {
            e.stopPropagation()
            onDismiss(n)
          }}
          className="absolute top-2 end-2 hidden rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground group-hover:block"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  )
}
